<template>
	<div class="feedback-container">
		<div class="feedback-form">
			<div class="form-wrap">
				<div class="feedback-form-item">
					<div class="feedback-form-item-label feedback-form-item-label--required">
						<span>{{ getFieldLabel('name') }}</span>
					</div>
					<div class="feedback-form-item-content feedback-form-item-content--textarea-inline">
						<van-field
							type="textarea"
							v-model="formData.name"
							:placeholder="getFieldTips('name')"
							maxlength="50"
							rows="1"
							autosize
						/>
					</div>
				</div>
				<div class="feedback-form-item">
					<cSearchPicker
						externalClass="c-search"
						:externalValue="formData.company"
						inputType="textarea"
						:placeholder="getFieldTips('company')"
						:maxlength="200"
						@select="inputCompany"
						@input="inputCompany"
					>
						<template>
							<div class="feedback-form-item-label feedback-form-item-label--required">
								<span>{{ getFieldLabel('company') }}</span>
							</div>
						</template>
					</cSearchPicker>
				</div>
				<div class="feedback-form-item">
					<div class="feedback-form-item-label">
						<span>{{ getFieldLabel('position') }}</span>
					</div>
					<div class="feedback-form-item-content feedback-form-item-content--input">
						<van-field
							v-model="formData.position"
							:placeholder="getFieldTips('position')"
							maxlength="200"
						/>
					</div>
				</div>
				<div class="feedback-form-item">
					<div class="feedback-form-item-label">
						<span>{{ getFieldLabel('phone') }}</span>
					</div>
					<div class="area-input" @click="getAreaCode">
						<span class="area-code">{{ formData.areaCode }}</span>
						<i class="icon-font drop-popup">&#xe68e;</i>
						<span class="area-separator"></span>
					</div>
					<div class="feedback-form-item-content feedback-form-item-content--input">
						<van-field
							v-model="formData.phone"
							:placeholder="getFieldTips('phone')"
							maxlength="11"
							type="tel"
							@input="validateNumber"
						/>
					</div>
				</div>
				<div class="feedback-form-item">
					<div class="feedback-form-item-label feedback-form-item-label--required">
						<span>{{ getFieldLabel('email') }}</span>
					</div>
					<div class="feedback-form-item-content feedback-form-item-content--input">
						<van-field v-model="formData.email" :placeholder="getFieldTips('email')" maxlength="128" />
					</div>
				</div>
				<div class="feedback-form-item">
					<div class="feedback-form-item-label feedback-form-item-label--required">
						<span>{{ getFieldLabel('verification') }}</span>
					</div>
					<div class="feedback-form-item-content feedback-form-item-content--input">
						<van-field
							v-model="formData.authCode"
							:placeholder="getFieldTips('verification')"
							maxlength="128"
						/>
					</div>
					<div class="feedback-form-item-suffix" @click="handleAuthCode">
						<span v-if="countdown === 0" class="suffix-text">{{ getFieldLabel('send-code') }}</span>
						<span v-else class="suffix-text suffix-text--disabled">{{ countdown }}S</span>
					</div>
				</div>
			</div>
		</div>
		<div class="feedback-footer">
			<div class="feedback-footer-btn" @click="cancelFeedback">{{ $t('enterprise.cancel') }}</div>
			<div :class="['feedback-footer-btn', disabled && 'disabled']" @click="submitFeedback">
				{{ $t('enterprise.submit') }}
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'enterprise_message_subscription_form'
};
</script>

<script setup>
import { ref, reactive, computed, getCurrentInstance, watch } from 'vue';
import { useStore, useRouter } from '@/utils/vue-api';
import { Field as VanField, Toast } from 'vant';
import { debounce } from '@/utils/api';
import { EN } from '@/assets/js/i18n';
import cSearchPicker from '@/view/investor_appointment/components/c-search-picker';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';
import { useNavigation } from '@rebuild/hooks/useNavigation';

const store = useStore();
const router = useRouter();
const { proxy } = getCurrentInstance();

const { message_subscription_messageMailCode, message_subscription_create } = useModuleData();

let __timer = null;

const formData = reactive({
	name: '',
	company: '',
	position: '',
	areaCode: '86',
	phone: '',
	email: '',
	authCode: ''
});

// 区号回填
watch(
	() => store.state.initialsAreaCode,
	data => {
		if (data) {
			const [prefix, code] = data.value.split('+');
			formData.areaCode = code;
		}
	}
);

const countdown = ref(0);

const messageMap = reactive({
	10015: proxy.$t('enterprise.normal-field.msg-box.10015'),
	10016: proxy.$t('enterprise.normal-field.msg-box.10016'),
	10017: proxy.$t('enterprise.normal-field.msg-box.10017'),
	10018: proxy.$t('enterprise.normal-field.msg-box.10018'),
	10019: proxy.$t('enterprise.normal-field.msg-box.10019'),
	10020: proxy.$t('enterprise.normal-field.msg-box.10020'),
	10021: proxy.$t('enterprise.normal-field.msg-box.10021')
});

const disabled = computed(() => {
	const required = ['name', 'company', 'email', 'authCode'];
	for (const key in formData) {
		if (required.includes(key) && !formData[key]) {
			return true;
		}
	}
	return false;
});

// 方法
const showTooltip = message => {
	store.dispatch('showTooltip', message);
};

const validateNumber = val => {
	// 通过正则表达式验证，只保留数字
	formData.phone = val.replace(/\D/g, '');
};

const getAreaCode = () => {
	router.push({
		name: 'areaCode_index',
		query: {
			areaCode: formData.areaCode
		}
	});
};

const getFieldLabel = prop => {
	return proxy.$i18n.t(`enterprise.normal-field.${prop}`);
};

const getFieldTips = prop => {
	if (prop === 'verification' && proxy.$i18n.locale === EN) return 'Please enter';
	return proxy.$i18n.t(`enterprise.normal-field.tips`, {
		label: proxy.$i18n.t(`enterprise.normal-field.${prop}`)
	});
};

const getFieldValidTips = prop => {
	return proxy.$i18n.t(`enterprise.normal-field.valid-tips`, {
		label: proxy.$i18n.t(`enterprise.normal-field.${prop}`)
	});
};

const inputCompany = ({ name }) => {
	formData.company = name;
};

const buildParams = () => {
	const params = { ...formData };
	params.areaCode = `+${params.areaCode}`;
	return params;
};

const handleError = err => {
	const errMsg = messageMap[err.code + ''];
	if (errMsg) {
		Toast(errMsg);
	} else {
		Toast(proxy.$t('enterprise.error'));
	}
};

const handleAuthCode = () => {
	if (countdown.value > 0) return;
	if (!formData.email) {
		return showTooltip(`${getFieldValidTips('email')}`);
	}
	const vaildErr = vaildateInput();
	if (vaildErr) {
		return showTooltip(vaildErr);
	}
	confirmAuthCode();
};

const confirmAuthCode = debounce(
	async function () {
		const params = buildParams();
		delete params.authCode;
		await message_subscription_messageMailCode.refresh(params);
		if (message_subscription_messageMailCode.error) {
			Toast.clear();
			return handleError(message_subscription_messageMailCode.error);
		}
		Toast.clear();
		showTooltip(proxy.$t('enterprise.normal-field.code-sent-to-email'));
		countdown.value = 60;
		__timer = setInterval(() => {
			if (countdown.value > 0) {
				countdown.value--;
			} else {
				clearInterval(__timer);
				__timer = null;
			}
		}, 1000);
	},
	300,
	true
);

const cancelFeedback = debounce(
	function () {
		router.back();
	},
	180,
	true
);

const { replaceModuleSubPath } = useNavigation();
const submitFeedback = debounce(
	async function () {
		if (disabled.value) return;
		const vaildErr = vaildateInput();
		if (vaildErr) {
			return showTooltip(vaildErr);
		}

		Toast.loading({
			message: 'Loading...',
			forbidClick: true,
			duration: 0
		});
		const params = buildParams();
		await message_subscription_create.refresh(params);
		if (message_subscription_create.error) {
			Toast.clear();
			return handleError(message_subscription_create.error);
		}
		Toast.clear();
		replaceModuleSubPath('message_subscription', 'result');
	},
	180,
	true
);

const vaildateInput = () => {
	if (formData.email) {
		const errEmail = !/^\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/.test(formData.email);
		if (errEmail) return proxy.$t('enterprise.field.success-email');
	}
	return null;
};
</script>

<style lang="less" scoped>
@import '@/style/mixins.less';

.c-search {
	/deep/.picker-list {
		z-index: 11;
	}
	/deep/.search-input {
		display: flex;
		align-items: center;
	}
	/deep/.text-area {
		text-align: left !important;
		min-height: 0.21rem;
		color: #1d2129;
		font-family: 'PingFang SC';
		font-size: 0.15rem;
		font-style: normal;
		font-weight: 400;
		line-height: 0.21rem;
	}
}

.feedback-container {
	height: 100%;
	overflow: hidden;
	display: flex;
	flex-direction: column;

	.feedback-form {
		flex: 1;
		margin-top: 0.07rem;
		padding: 0 0.14rem;
		overflow-y: auto;

		.form-wrap {
			padding: 0 0.12rem;
			background: #fff;
			&:not(:first-child) {
				margin-top: 12px;
			}
		}
		.feedback-form-item {
			display: flex;
			flex-wrap: wrap;
			justify-content: space-between;

			&:not(:last-child) {
				.thinner-border-bottom(#e8eaef);

				&:before {
					pointer-events: none;
					z-index: 1;
				}
			}
		}
		.feedback-form-item-label {
			padding: 0.16rem;
			padding-left: 0;
			width: 1.1rem;
			word-break: break-all;

			span {
				display: inline-flex;
				line-height: 0.21rem;
				color: #666;
				font-family: 'PingFang SC';
				font-size: 0.15rem;
				font-style: normal;
				font-weight: 400;
			}
			&--full {
				width: 100%;
			}
			&--required {
				span {
					&::after {
						content: '*';
						margin-left: 0.04rem;
						color: #ff4545;
						pointer-events: none;
					}
				}
			}
			&--textarea {
				padding: 0.12rem 0 0.12rem 0;
			}
		}
		.feedback-form-item-content {
			flex: 1;
			font-size: 0;

			.before-placeholder {
				font-size: 12px;
				height: 0;
			}
		}
		/deep/ .feedback-form-item-content--textarea {
			padding-bottom: 0.12rem;
			.van-cell {
				padding: 0.12rem;
				.thinner-border-around(#e8eaef, 0.04rem);

				&:before {
					pointer-events: none;
					z-index: 1;
				}
			}
			.van-field__control {
				min-height: 1.5rem;
				color: #1d2129;
				font-family: 'PingFang SC';
				font-size: 0.15rem;
				font-style: normal;
				font-weight: 400;
				line-height: 0.21rem;

				&::placeholder,
				&::-webkit-input-placeholder {
					color: #c5c5c5;
				}
			}
		}
		/deep/ .feedback-form-item-content--textarea-inline {
			.van-cell {
				padding: 0.12rem 0;
			}
			.van-field__control {
				color: #1d2129;
				font-family: 'PingFang SC';
				font-size: 0.15rem;
				font-style: normal;
				font-weight: 400;
				line-height: 0.21rem;

				&::placeholder,
				&::-webkit-input-placeholder {
					color: #c5c5c5;
				}
			}
		}
		/deep/ .feedback-form-item-content--input {
			.van-cell {
				padding: 0.12rem 0;
				height: 100%;
			}
			.van-field__body,
			.van-field__control {
				height: 100%;
			}
			.van-field__control {
				color: #1d2129;
				font-family: 'PingFang SC';
				font-size: 0.15rem;
				font-style: normal;
				font-weight: 400;
				line-height: 0.21rem;

				&::placeholder,
				&::-webkit-input-placeholder {
					color: #c5c5c5;
				}
			}
		}

		.area-input {
			display: flex;
			align-items: center;
			justify-content: flex-end;
			font-size: 0;

			.area-code {
				color: #333;
				font-family: 'PingFang SC';
				font-size: 0.14rem;
				font-style: normal;
				font-weight: 400;
				line-height: 0.18rem; /* 128.571% */
			}
			.drop-popup {
				margin-left: 0.08rem;
				font-size: 0.1rem;
				color: #c5c5c5;
			}
			.area-separator {
				margin: 0 0.18rem;
				width: 0.01rem;
				height: 0.18rem;
				background: #e8eaef;
			}
		}
		.feedback-form-item-suffix {
			margin-left: 0.12rem;
			display: flex;
			justify-content: center;
			align-items: center;
			text-align: right;

			.suffix-text {
				color: var(--ir-theme-color);
				text-align: right;
				font-family: 'PingFang SC';
				font-size: 0.14rem;
				font-style: normal;
				font-weight: 400;
				line-height: 0.18rem; /* 128.571% */

				&--disabled {
					opacity: 0.6;
				}
			}
		}
	}
	.feedback-footer {
		background: #fff;
		padding: 0.12rem 0.14rem;
		padding-bottom: calc(0.12rem + constant(safe-area-inset-bottom));
		padding-bottom: calc(0.12rem + env(safe-area-inset-bottom));
		box-shadow: 0px -0.03rem 0.07rem rgba(0, 0, 0, 0.02);
		display: flex;
		justify-content: space-between;

		.feedback-footer-btn {
			width: calc(50% - 0.06rem);
			text-align: center;
			line-height: 0.42rem;
			font-family: 'PingFang SC';
			font-size: 0.16rem;
			font-style: normal;
			font-weight: 400;
			border-radius: 0.01rem;

			&:nth-child(1) {
				background: #f6f7f8;
				color: #666;
			}
			&:nth-child(2) {
				background: var(--ir-theme-color);
				color: #fff;
			}
		}
		.disabled {
			opacity: 0.5;
		}
	}
}
</style>
